<template>
    <div class="product-v3-container">
        <!-- Stats Header Cards -->
        <ProductStatsNavigation :stats="stats" activeTab="products" @click="navigateToIndex" />
        <v-app class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
            <!-- Header -->
            <div class="d-flex flex-row align-items-center" :style="{ marginTop: '10px', marginBottom: normalizeSize(20, true) }">
                <h1
                    class="ppv2-text-black poppinsRegular uppercaseFirstLetter mb-0 title-header"
                    :style="{ fontSize: normalizeFontSize(26) }"
                >
                    {{ isEdit ? $t('product.edit_title') : $t('product.add_title') }}
                </h1>
            </div>

            <div class="ppv2-w-full">
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                    <!-- Left Column -->
                    <div class="ppv2-w-full md:ppv2-w-[63%]">
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-p-6 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-flex-wrap">
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        :label="$t('product.name')"
                                        v-model="form.name"
                                        :placeholder="$t('product.name_placeholder')"
                                        :required="true"
                                        :errorText="errors.name"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                </div>
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <BaseSelect
                                        :label="$t('product.category')"
                                        v-model="form.category_selection"
                                        :options="categoryOptions"
                                        :placeholder="$t('product.category_placeholder')"
                                        :required="true"
                                        :errorText="
                                            errors.category_id
                                                ? Array.isArray(errors.category_id)
                                                    ? errors.category_id[0]
                                                    : errors.category_id
                                                : ''
                                        "
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                </div>
                                <div class="ppv2-w-full ppv2-px-3">
                                    <BaseTextarea
                                        :label="$t('product.description')"
                                        v-model="form.description"
                                        :placeholder="$t('product.description_placeholder')"
                                        :rows="4"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        :errorText="errors.description ? errors.description[0] : ''"
                                    />
                                </div>
                                <div class="ppv2-w-full ppv2-px-3">
                                    <div class="ppv2-mt-4 ppv2-text-center">
                                        <CustomInputUpload
                                            :label="$t('product.usage')"
                                            v-model="form.image"
                                            @input="onImageUpload"
                                            :maxSize="15 * 1024 * 1024"
                                        />
                                    </div>
                                </div>
                            </div>
                        </v-card>

                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('product.pricing') }}
                                </h2>
                                <BaseSwitch
                                    :label="$t('product.apply_discount')"
                                    v-model="form.discount"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                />
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>
                            <div class="ppv2-flex ppv2-flex-wrap ppv2-p-6">
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <CustomInput
                                        :label="$t('product.sale_price')"
                                        v-model="form.price"
                                        type="number"
                                        placeholder="0.00"
                                        :rightIcon="$symbolCurrency"
                                        :required="true"
                                        :min="0"
                                        step="0.01"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        :errorText="errors.price"
                                    />
                                </div>
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                    <BaseSelect
                                        :label="$t('product.vat_rate')"
                                        v-model="form.vat_rate"
                                        :options="vatOptions"
                                        placeholder="20%"
                                        :required="true"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        :errorText="errors.vat_rate ? errors.vat_rate[0] : ''"
                                    />
                                </div>
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3" v-if="form.discount">
                                    <CustomInput
                                        :label="$t('product.discount_percentage')"
                                        v-model="discountPercentage"
                                        type="number"
                                        placeholder="0"
                                        rightIcon="%"
                                        :min="0"
                                        :max="100"
                                        step="0.01"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        @input="onDiscountPercentageInput"
                                    />
                                </div>
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3" v-if="form.discount">
                                    <CustomInput
                                        :label="$t('product.new_price')"
                                        v-model="form.discount_price"
                                        type="number"
                                        placeholder="0.00"
                                        :rightIcon="$symbolCurrency"
                                        :min="0"
                                        step="0.01"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                        :errorText="errors.discount_price ? errors.discount_price[0] : ''"
                                        @input="onDiscountPriceInput"
                                    />
                                </div>
                            </div>
                        </v-card>

                        <!-- Variantes et caractéristiques -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('product.variants_features') }}
                                </h2>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>
                            <div class="ppv2-flex ppv2-flex-wrap ppv2-p-6 ppv2-mx-3">
                                <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3 ppv2-mb-[20px]">
                                    <BaseSelect
                                        :label="$t('product.available_sizes')"
                                        v-model="form.available_sizes"
                                        :options="sizeOptions"
                                        multiple
                                        :placeholder="$t('product.sizes_placeholder')"
                                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                        customLabelClass="poppinsLight ppv2-text-black"
                                    />
                                </div>
                                <div class="ppv2-w-full ppv2-flex ppv2-flex-wrap">
                                    <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                        <BaseSelect
                                            :label="$t('product.unit')"
                                            v-model="form.size_type"
                                            :options="capacityOptions"
                                            :placeholder="$t('product.unit_placeholder')"
                                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            customLabelClass="poppinsLight ppv2-text-black"
                                        />
                                    </div>
                                    <div class="ppv2-w-full md:ppv2-w-1/2 ppv2-px-3">
                                        <CustomInput
                                            :label="$t('product.value')"
                                            v-model="form.weight"
                                            type="number"
                                            placeholder="15"
                                            :min="1"
                                            step="any"
                                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            customLabelClass="poppinsLight ppv2-text-black"
                                        />
                                    </div>
                                </div>
                            </div>
                        </v-card>
                    </div>

                    <!-- Right Column -->
                    <div class="ppv2-w-full md:ppv2-w-[35%]">
                        <!-- Status Card -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-p-6 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-space-y-4">
                                <BaseSwitch
                                    :label="$t('product.active')"
                                    v-model="form.active"
                                    :boxed="true"
                                    variant="success"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                />
                                <BaseSwitch
                                    :label="$t('product.visible_online')"
                                    v-model="form.visible_online"
                                    :boxed="true"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                />
                            </div>

                            <div v-if="isEdit" class="ppv2-mt-6">
                                <h3
                                    class="poppinsSemiBold ppv2-text-black ppv2-mb-3"
                                    :style="{ fontSize: normalizeFontSize(16) }"
                                >
                                    {{ $t('product.history') }}
                                </h3>
                                <div v-if="filteredHistory.length > 0" class="ppv2-space-y-2">
                                    <p
                                        v-for="(history, index) in filteredHistory"
                                        :key="index"
                                        class="poppinsLight ppv2-text-[#6B7280]"
                                        :style="{ fontSize: normalizeFontSize(13) }"
                                    >
                                        {{ history.generated_message }}
                                    </p>
                                </div>
                                <a
                                    v-if="isEdit && product.visible_online && appUrlFront"
                                    :href="`${appUrlFront}/shop/${product.category_id}/${product.id}`"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-mt-4 ppv2-text-[#7D2DBD] poppinsRegular"
                                    :style="{ fontSize: normalizeFontSize(14) }"
                                >
                                    {{ $t('product.access_product_sheet') }}
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M6 12L10 8L6 4"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </a>
                            </div>
                        </v-card>

                        <!-- Approvisionnement -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('product.supply') }}
                                </h2>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>
                            <div class="ppv2-p-6 ppv2-space-y-4">
                                <BaseSelect
                                    :label="$t('product.supplier')"
                                    v-model="form.supplier_id"
                                    :options="supplierOptions"
                                    :placeholder="$t('product.supplier_placeholder')"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    clearable
                                />
                                <BaseSelect
                                    :label="$t('product.brand')"
                                    v-model="form.brand_id"
                                    :options="brandOptions"
                                    :placeholder="$t('product.brand_placeholder')"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                    clearable
                                />
                                <CustomInput
                                    :label="$t('product.supplier_reference')"
                                    v-model="form.supplier_reference"
                                    placeholder="ex: JB-20050"
                                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                    customLabelClass="poppinsLight ppv2-text-black"
                                />
                                <div class="ppv2-flex ppv2-flex-wrap ppv2--mx-2">
                                    <div class="ppv2-w-1/2 ppv2-px-2">
                                        <CustomInput
                                            :label="$t('product.purchase_cost')"
                                            v-model="form.purchase_cost"
                                            type="number"
                                            placeholder="ex: 100"
                                            :rightIcon="$symbolCurrency"
                                            :min="0"
                                            step="0.01"
                                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            customLabelClass="poppinsLight ppv2-text-black"
                                        />
                                    </div>
                                    <div class="ppv2-w-1/2 ppv2-px-2">
                                        <CustomInput
                                            :label="$t('product.recommanded_quantity')"
                                            v-model="form.recommanded_quantity"
                                            type="number"
                                            placeholder="ex: 1"
                                            :min="0"
                                            step="1"
                                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            customLabelClass="poppinsLight ppv2-text-black"
                                        />
                                    </div>
                                </div>
                            </div>
                        </v-card>

                        <!-- Stock -->
                        <v-card
                            class="!ppv2-rounded-[10px] ppv2-border !ppv2-border-black !ppv2-border-opacity-20 ppv2-mb-6 ppv2-shadow-none"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-p-6">
                                <h2 class="poppinsSemiBold ppv2-text-black mb-0" :style="{ fontSize: normalizeFontSize(18) }">
                                    {{ $t('product.stock') }}
                                </h2>
                            </div>
                            <v-divider class="ppv2-m-0"></v-divider>
                            <div class="ppv2-p-6 ppv2-space-y-4">
                                <div class="ppv2-flex ppv2-flex-wrap ppv2--mx-2">
                                    <div class="ppv2-w-1/2 ppv2-px-2">
                                        <CustomInput
                                            :label="$t('product.stock_quantity')"
                                            v-model="form.quantity"
                                            type="number"
                                            placeholder="ex: 100"
                                            :min="0"
                                            step="1"
                                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            customLabelClass="poppinsLight ppv2-text-black"
                                        />
                                        <p class="poppinsLight ppv2-text-xs ppv2-text-black ppv2-opacity-60 ppv2-mt-1 ppv2-mb-0">
                                            {{ $t('product.stock_quantity_help') }}
                                        </p>
                                    </div>
                                    <div class="ppv2-w-1/2 ppv2-px-2">
                                        <CustomInput
                                            :label="$t('product.threshold_alert')"
                                            v-model="form.threshold_alert"
                                            type="number"
                                            placeholder="ex: 1"
                                            :min="0"
                                            step="1"
                                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                                            customLabelClass="poppinsLight ppv2-text-black"
                                        />
                                    </div>
                                </div>
                            </div>
                        </v-card>
                    </div>
                </div>
                <div class="ppv2-flex ppv2-flex-col md:ppv2-flex-row" :style="{ gap: '2%' }">
                    <!-- Bottom Actions -->
                    <div class="ppv2-w-full md:ppv2-w-[63%]">
                        <div
                            class="ppv2-flex ppv2-justify-between ppv2-items-center"
                            :style="{ marginTop: normalizeSize(24), marginBottom: normalizeSize(40, true) }"
                        >
                            <BackButton type="button" @click="cancel" />
                            <BaseButton
                                variant="primary"
                                :loading="isSaving"
                                @click="save"
                                :customHeight="48"
                                customClass="!ppv2-min-w-[160px] !ppv2-bg-[#7D2DBD] hover:!ppv2-bg-[#6b26a3]"
                            >
                                {{ $t('product.validate') }}
                            </BaseButton>
                        </div>
                    </div>
                </div>
            </div>
        </v-app>

        <!-- Exit Dialog -->
        <CustomDialog v-model="showExitPopup" :defaultTitle="$t('product.exit_dialog.title')" heightClass="ppv2-max-h-[40vh]">
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('product.exit_dialog.message') }}
                </p>
            </template>
            <template #actions>
                <BaseButton variant="outline" @click="exit">{{ $t('product.exit_dialog.exit') }}</BaseButton>
                <BaseButton variant="primary" @click="showExitPopup = false">{{ $t('product.exit_dialog.stay') }} </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import CustomInput from '../../Inputs/CustomInput.vue'
import CustomInputUpload from '../../Inputs/CustomInputUpload.vue'
import BackButton from '../../Shared/BackButton.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import BaseTextarea from '../../Shared/BaseTextarea.vue'
import BaseSelect from '../../Shared/BaseSelect.vue'
import BaseSwitch from '../../Shared/BaseSwitch.vue'
import BaseCheckbox from '../../Shared/BaseCheckbox.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import ProductStatsNavigation from './ProductStatsNavigation.vue'
import normalizeSize from '../../../utils/normalizeSize'
import axios from 'axios'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'ProductForm',
    components: {
        CustomInput,
        CustomInputUpload,
        BackButton,
        BaseButton,
        BaseTextarea,
        BaseSelect,
        BaseSwitch,
        BaseCheckbox,
        CustomDialog,
        ProductStatsNavigation,
    },
    props: {
        product: { type: Object, default: () => null },
        cats: { type: Array, default: () => [] },
        brands: { type: Array, default: () => [] },
        suppliers: { type: Array, default: () => [] },
        available_sizes: { type: Array, default: () => [] },
        tags: { type: Object, default: () => ({}) },
        countryVatRates: { type: Array, default: () => [] },
        capacities: { type: Array, default: () => [] },
        appUrlFront: { type: String, default: '' },
        userId: { type: [Number, String], default: null },
        token: { type: [String], default: null },
    },
    data() {
        return {
            isSaving: false,
            showExitPopup: false,
            discountPercentage: 0, // Pourcentage de réduction calculé
            form: {
                id: null,
                name: '',
                category_id: null,
                subcategory_id: null,
                category_selection: null,
                description: '',
                usage: '',
                image: null,
                price: '0',
                vat_rate: 0,
                discount: false,
                discount_price: '',
                available_sizes: [],
                size_type: null,
                weight: '',
                active: true,
                visible_online: true,
                supplier_id: null,
                supplier_reference: '',
                brand_id: null,
                purchase_cost: '',
                recommanded_quantity: 1,
                quantity: null,
                threshold_alert: null,
                code_ean: '',
                chip_id: '',
                tags: [],
            },
            stats: {
                products: 0,
                categories: 0,
                suppliers: 0,
                brands: 0,
            },
            errors: {},
            initialForm: null,
            sizes: {
                title: normalizeSize(24, true),
                label: normalizeSize(15, true),
                text: normalizeSize(14, true),
            },
        }
    },
    computed: {
        isEdit() {
            return !!(this.product && this.product.id)
        },
        filteredHistory() {
            if (!this.product || !this.product.product_history) return []

            const history = this.product.product_history
            const result = []

            // Trouver le premier "created"
            const created = history.find((h) => h.action === 'created')
            if (created) result.push(created)

            // Trouver le dernier "updated"
            const updates = history.filter((h) => h.action === 'updated')
            if (updates.length > 0) {
                const latestUpdate = updates[updates.length - 1]
                // Ne pas ajouter si c'est le même que "created" (cas rare mais possible si l'action est renommée)
                if (!created || latestUpdate.id !== created.id) {
                    result.push(latestUpdate)
                }
            }

            return result
        },
        categoryOptions() {
            const options = []
            this.cats.forEach((cat) => {
                options.push({ label: cat.name, value: `cat_${cat.id}` })
                if (cat.children && cat.children.length > 0) {
                    cat.children.forEach((sub) => {
                        options.push({ label: `- ${sub.name}`, value: `sub_${sub.id}` })
                    })
                } else if (cat.subcategories && cat.subcategories.length > 0) {
                    cat.subcategories.forEach((sub) => {
                        options.push({ label: `- ${sub.name}`, value: `sub_${sub.id}` })
                    })
                }
            })
            return options
        },
        brandOptions() {
            let filteredBrands = this.brands
            if (this.form.supplier_id) {
                filteredBrands = this.brands.filter((b) => b.suppliers && b.suppliers.some((s) => s.id === this.form.supplier_id))
            }
            const options = filteredBrands.map((b) => ({ label: b.name, value: b.id }))
            options.unshift({ label: 'Aucun', value: 0 })
            return options
        },
        supplierOptions() {
            let filteredSuppliers = this.suppliers
            if (this.form.brand_id) {
                filteredSuppliers = this.suppliers.filter((s) => s.brands && s.brands.some((b) => b.id === this.form.brand_id))
            }
            const options = filteredSuppliers.map((s) => ({ label: s.name, value: s.id }))
            options.unshift({ label: 'Aucun', value: 0 })
            return options
        },
        sizeOptions() {
            return this.available_sizes.map((s) => ({ label: `${s.slug} - ${s.name}`, value: s.id }))
        },
        capacityOptions() {
            return this.capacities.map((c) => ({ label: c.name || c, value: c.id || c }))
        },
        vatOptions() {
            return this.countryVatRates.map((v) => ({ label: `${v.pourcentage}%`, value: parseFloat(v.pourcentage) }))
        },
    },
    watch: {
        'form.price'(newPrice) {
            // Quand le prix de base change, recalculer le nouveau prix si un pourcentage existe
            const price = parseFloat(newPrice) || 0
            const percentage = parseFloat(this.discountPercentage) || 0

            if (price > 0 && percentage > 0) {
                this.form.discount_price = (price * (1 - percentage / 100)).toFixed(2)
            }
        },
        'form.discount'(newVal) {
            if (!newVal) {
                this.form.discount_price = ''
                this.discountPercentage = 0
            }
        },
        'form.supplier_id'(newVal) {
            // Le champ Référence fournisseur doit rester vide : c'est le client qui le saisit
        },
        'form.brand_id'(newVal) {
            // Optionnel: si une marque est choisie et qu'elle n'a qu'un seul fournisseur, on peut le pré-sélectionner
            if (newVal && !this.form.supplier_id) {
                const brand = this.brands.find((b) => b.id === newVal)
                if (brand && brand.suppliers && brand.suppliers.length === 1) {
                    this.form.supplier_id = brand.suppliers[0].id
                }
            }
        },
        'form.category_selection'(newVal) {
            if (newVal) {
                if (newVal.startsWith('sub_')) {
                    const subId = parseInt(newVal.replace('sub_', ''))
                    this.form.subcategory_id = subId
                    // Find parent category
                    const parentCat = this.cats.find(
                        (c) =>
                            (c.subcategories && c.subcategories.some((s) => s.id === subId)) ||
                            (c.children && c.children.some((s) => s.id === subId))
                    )
                    if (parentCat) {
                        this.form.category_id = parentCat.id
                    }
                } else if (newVal.startsWith('cat_')) {
                    this.form.category_id = parseInt(newVal.replace('cat_', ''))
                    this.form.subcategory_id = null
                }
            } else {
                this.form.category_id = null
                this.form.subcategory_id = null
            }
        },
    },
    created() {
        if (this.product) {
            this.initForm()
        }
        this.initialForm = JSON.stringify(this.form)
    },
    mounted() {
        // Remove white background from parent container
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.add('food-v3-no-bg')
        }
        this.fetchStats()
    },
    beforeDestroy() {
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.remove('food-v3-no-bg')
        }
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        initForm() {
            this.form = {
                ...this.form,
                ...this.product,
                image: this.product.image_url, // Utiliser img1 directement (URL complète en base)
                discount: !!this.product.discount,
                active: !!this.product.active,
                visible_online: !!this.product.visible_online,
                tags: this.product.tags ? this.product.tags.map((t) => t.id) : [],
                available_sizes: this.product.available_sizes ? this.product.available_sizes.map((s) => s.id) : [],
            }

            // Supprimer les champs obsolètes
            delete this.form.is_cabin
            delete this.form.is_revent
            delete this.form.consumption

            // Si le prix TTC n'est pas renseigné mais que le prix HT l'est, on le calcule
            if (!this.form.price && this.form.price_ht) {
                const priceHt = parseFloat(this.form.price_ht) || 0
                const vatRate = parseFloat(this.form.vat_rate) || 0
                this.form.price = (priceHt * (1 + vatRate / 100)).toFixed(2)
            }

            // Initialiser la sélection de catégorie
            if (this.product.subcategory_id) {
                this.form.category_selection = `sub_${this.product.subcategory_id}`
            } else if (this.product.category_id) {
                this.form.category_selection = `cat_${this.product.category_id}`
            }

            // Calculer le pourcentage de réduction initial
            this.calculateDiscountPercentage()
        },

        onImageUpload(file) {
            this.form.image = file
        },
        flattenTags(categoryTags) {
            let tags = []
            if (Array.isArray(categoryTags)) {
                tags = categoryTags
            } else {
                Object.values(categoryTags).forEach((subTags) => {
                    if (Array.isArray(subTags)) {
                        tags = [...tags, ...subTags]
                    } else if (typeof subTags === 'object') {
                        tags.push(subTags)
                    }
                })
            }
            return tags
        },
        formatDate(date) {
            if (!date) return '-'
            const locale = this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US'
            return new Date(date).toLocaleDateString(locale, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            })
        },
        calculateDiscountPercentage() {
            // Calculer le pourcentage basé sur price et discount_price
            const price = parseFloat(this.form.price) || 0
            const discountPrice = parseFloat(this.form.discount_price) || 0

            if (price > 0 && discountPrice > 0 && discountPrice < price) {
                this.discountPercentage = (((price - discountPrice) / price) * 100).toFixed(2)
            } else {
                this.discountPercentage = 0
            }
        },
        onDiscountPriceInput(value) {
            // Quand le nouveau prix change, recalculer le pourcentage
            const price = parseFloat(this.form.price) || 0
            const discountPrice = parseFloat(value) || 0

            // Vérifier que le nouveau prix est inférieur au prix de base
            if (discountPrice >= price && price > 0) {
                this.$toast.warning(this.$t('product.notifications.price_warning'))
            }

            // Calculer le pourcentage
            if (price > 0 && discountPrice > 0 && discountPrice < price) {
                this.discountPercentage = (((price - discountPrice) / price) * 100).toFixed(2)
            } else {
                this.discountPercentage = 0
            }
        },
        onDiscountPercentageInput(value) {
            // Quand le pourcentage change, recalculer le nouveau prix
            const price = parseFloat(this.form.price) || 0
            let percentage = parseFloat(value) || 0

            // Limiter entre 0 et 100 (pour éviter un prix négatif)
            if (percentage < 0) percentage = 0
            if (percentage >= 100) {
                percentage = 99.99 // On évite un prix de 0 ou négatif
                this.$toast.warning(this.$t('product.notifications.price_warning'))
            }
            this.discountPercentage = percentage

            // Calculer le nouveau prix
            if (price > 0 && percentage > 0) {
                this.form.discount_price = (price * (1 - percentage / 100)).toFixed(2)
            } else {
                this.form.discount_price = ''
            }
        },
        validateForm() {
            const errors = {}

            // Champs requis
            if (!this.form.name || this.form.name.trim() === '') {
                errors.name = [this.$t('product.validation.name_required')]
            }

            if (!this.form.category_id && !this.form.category_selection) {
                errors.category_id = [this.$t('product.validation.category_required')]
            }

            const price = parseFloat(this.form.price)
            if (isNaN(price) || price < 0) {
                errors.price = [this.$t('product.validation.price_required')]
            }

            if (this.form.vat_rate === null || this.form.vat_rate === undefined || this.form.vat_rate === '') {
                errors.vat_rate = [this.$t('product.validation.vat_required')]
            }

            // Validation de la remise
            if (this.form.discount) {
                const discountPrice = parseFloat(this.form.discount_price)
                if (isNaN(discountPrice) || discountPrice < 0) {
                    errors.discount_price = [this.$t('product.validation.price_required')]
                } else if (discountPrice >= price) {
                    errors.discount_price = [this.$t('product.notifications.price_warning')]
                }
            }

            return errors
        },
        async save() {
            // Validation côté front
            this.errors = this.validateForm()

            if (Object.keys(this.errors).length > 0) {
                // Afficher le premier message d'erreur
                const firstError = Object.values(this.errors)[0][0]
                this.$toast.error(firstError)
                this.isSaving = false
                return
            }

            this.isSaving = true
            this.errors = {}

            try {
                const formData = new FormData()
                Object.keys(this.form).forEach((key) => {
                    if (key === 'tags' || key === 'available_sizes') {
                        this.form[key].forEach((val) => formData.append(`${key}[]`, val))
                    } else if (key === 'image' && this.form[key] instanceof File) {
                        formData.append('image', this.form[key])
                    } else if (key === 'supplier_id' || key === 'brand_id') {
                        // Allow sending null/empty to deselect
                        formData.append(key, this.form[key] || '')
                    } else if (key === 'quantity' || key === 'threshold_alert') {
                        // Toujours envoyé : vide = stock illimité (le serveur le passe à NULL), 0 reste 0
                        formData.append(key, this.form[key] ?? '')
                    } else if (this.form[key] !== null && this.form[key] !== '') {
                        formData.append(key, this.form[key])
                    }
                })
                formData.append('user_id', this.userId)

                const url = this.isEdit ? `/api/v3/product/${this.form.id}` : '/api/v3/product'

                if (this.isEdit) {
                    formData.append('_method', 'PATCH')
                }

                await axios.post(url, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${this.token}`,
                    },
                })

                this.$toast.success(
                    this.isEdit ? this.$t('product.notifications.update_success') : this.$t('product.notifications.add_success')
                )
                //this.exit()
            } catch (error) {
                // Gestion des erreurs 422 (validation)
                if (error.response && error.response.status === 422) {
                    const responseErrors = error.response.data.errors
                    this.errors = responseErrors

                    // Afficher les erreurs de champ une par une
                    let index = 0
                    Object.keys(responseErrors).forEach((field) => {
                        responseErrors[field].forEach((message) => {
                            setTimeout(() => {
                                this.$toast.error(message, { duration: 5000 })
                            }, index * 300)
                            index++
                        })
                    })
                } else if (error.response && error.response.data && error.response.data.message) {
                    // Autres erreurs avec message
                    this.$toast.error(error.response.data.message)
                } else {
                    // Erreur générique
                    this.$toast.error(this.$t('product.notifications.save_error'))
                }
            } finally {
                this.isSaving = false
            }
        },
        cancel() {
            if (JSON.stringify(this.form) !== this.initialForm) {
                this.showExitPopup = true
            } else {
                this.exit()
            }
        },
        exit() {
            window.location.href = '/back-office/v3/product'
        },
        navigateToIndex(tab) {
            window.location.href = `/back-office/v3/product?tab=${tab}`
        },
        async fetchStats() {
            try {
                const response = await axios.get('/api/v3/product/stats')
                this.stats = response.data
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
    },
}
</script>

<style scoped>
.v-card {
    transition: all 0.2s ease-in-out;
}

.ppv2-space-y-4 > * + * {
    margin-top: 1rem;
}

.ppv2-space-y-2 > * + * {
    margin-top: 0.5rem;
}
</style>
